import { useMutation } from '@apollo/client'
import { TextInput } from '@designsforhealth/dfh-react-components'
import { Button } from '@dfh-corp-sites/ui'
import { CircularProgress } from '@material-ui/core'
import * as R from 'ramda'
import React, { useContext, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import styled from 'styled-components'

import {
  ApplyCouponToCartDocument,
  SetDispensaryPromoCodeOnCartDocument,
} from '../../graphql/magento'
import useIsMounted from '../../hooks/useIsMounted'
import { TokenContext } from '../../layouts/context'
import {
  appendCartDispensaryPromoCode,
  CartDispensaryPromoCode,
  writeCartDispensaryPromoCodes,
} from '../../lib/cart/dispensary-promo'
import { invalidCouponCodeMessage, messageFromAddCouponError } from '../../lib/cart/errors'
import { usePromoCodeUtilities } from '../../lib/cart/hooks/usePromoCodeUtilities'
import { logAndCaptureException } from '../../utils/errorTools'
import { cn } from '../utils/cn'

const StyledTextInput = styled(TextInput)`
  margin-bottom: 0;
  input {
    background-color: ${({ theme }) => theme.colors.grayscale.white};
    // compress height of input slightly
    height: 52px;
    padding-top: 12px;
    border: 1px solid
      ${({ errors, name, theme }) =>
        errors && errors[name] ? theme.colors.semantic.error : theme.colors.coolGray.cool250};
  }
  label {
    // re-center label after adjusting input height
    top: 20px;
  }
`

const StyledFieldError = styled.div`
  color: ${({ theme }) => theme.colors.semantic.error};
  flex-basis: 100%;
  margin-bottom: 10px;
`

export interface AddPromoCodeProps {
  cartDispensaryPromoCodes: CartDispensaryPromoCode[] | undefined
  cartId: string
  className?: string
  formDisabled: boolean
  isEmptyCart: boolean
}

const AddPromoCode: React.FC<AddPromoCodeProps> = ({
  cartDispensaryPromoCodes,
  cartId,
  className,
  formDisabled,
  isEmptyCart,
}) => {
  const token = useContext(TokenContext)
  const { errors, handleSubmit, register, reset, setError } = useForm<{
    promoCode?: string
  }>()
  const [addCoupon, { loading: applyCouponLoading }] = useMutation(ApplyCouponToCartDocument, {
    context: { token },
  })
  const [setDispensaryPromoCode, { loading: setDispensaryPromoCodeLoading }] = useMutation(
    SetDispensaryPromoCodeOnCartDocument,
    {
      context: { token },
    },
  )
  const [mockLoading, setMockLoading] = useState(false)
  const isMounted = useIsMounted()
  const {
    isCodeRestrictedFromUIInput,
    loading: utilitiesLoading,
    translateCode,
  } = usePromoCodeUtilities({ cartDispensaryPromoCodes })

  const setInvalidCouponCodeAfterDelay = () => {
    setMockLoading(true)
    // simulate request
    setTimeout(() => {
      if (!isMounted.current) {
        return
      }
      setError('promoCode', { type: 'validate', message: invalidCouponCodeMessage })
      setMockLoading(false)
    }, 3000)
  }

  const onSubmit: SubmitHandler<{ promoCode: string }> = R.pipe(
    ({ promoCode }) => promoCode ?? '',
    R.toUpper,
    R.trim,
    async (promoCode): Promise<void> => {
      // instead of requiring the field and displaying an error state when empty, just
      // check for non-empty value on submit
      if (!promoCode) {
        reset()
        return
      }

      if (await isCodeRestrictedFromUIInput(promoCode)) {
        setInvalidCouponCodeAfterDelay()
        return
      }

      const translateCodeResult = await translateCode(promoCode)

      const { errors: mutationErrors } = await addCoupon({
        variables: {
          cartId,
          couponCode:
            translateCodeResult._tag === 'translated'
              ? translateCodeResult.translatedCode
              : translateCodeResult.inputCode,
        },
      })

      const [mutationError] = mutationErrors ?? []
      // Do not reset form when operation unsuccessful
      if (mutationError) {
        setError('promoCode', {
          type: 'server',
          message: messageFromAddCouponError(mutationError),
        })
        return
      }

      if (translateCodeResult._tag === 'translated') {
        await setDispensaryPromoCode({
          variables: {
            cartId,
            dispensaryPromoCode: writeCartDispensaryPromoCodes(
              appendCartDispensaryPromoCode(
                {
                  dispensaryCode: translateCodeResult.inputCode,
                  rootCode: translateCodeResult.translatedCode,
                },
                cartDispensaryPromoCodes ?? [],
              ),
            ),
          },
        })
      }

      reset()
    },
    R.otherwise((rawErr: unknown) => {
      // ensure error is instance of Error
      const err: Error = rawErr instanceof Error ? rawErr : new Error(`addCoupon: ${rawErr}`)
      // log and capture errors (e.g. Web Crypto API errors)
      logAndCaptureException(err)
      setError('promoCode', {
        type: 'server',
        message: 'An application error occurred, and we have been notified.',
      })
    }),
  )

  const disableAddPromoCode =
    applyCouponLoading ||
    isEmptyCart ||
    formDisabled ||
    mockLoading ||
    setDispensaryPromoCodeLoading ||
    utilitiesLoading

  return (
    <form className={cn('p-5', className)} onSubmit={handleSubmit(onSubmit)}>
      <div className="flex gap-2 w-full">
        <StyledTextInput
          defaultValue=""
          disabled={disableAddPromoCode}
          errors={errors}
          label="Promo code"
          name="promoCode"
          register={register}
        />
        <Button
          disabled={disableAddPromoCode}
          onClick={handleSubmit(onSubmit)}
          type="submit"
          size={'sm'}
          className="m-auto"
        >
          {applyCouponLoading || mockLoading || setDispensaryPromoCodeLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Apply'
          )}
        </Button>
      </div>
      {errors.promoCode && <StyledFieldError>{errors.promoCode.message}</StyledFieldError>}
    </form>
  )
}

const StyledAddPromoCode = styled(AddPromoCode)`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;
  justify-content: space-between;
  position: relative;
  width: 100%;
`

export default StyledAddPromoCode
